import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import { DataTable, EmptyState, LoadingButton, Notice, PageHeader, StatusChip } from '@routes-plan/ui';
import { AdminPage, ContentCard, useAlert } from '../shared/AdminUi';
import StandardModal from '../../../components/modales/StandardModal';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { setPageTitle } from '../../../utils/pageTitle';
import { companyLocale, formatNumber } from '../../../utils/format/currency';
import { apiErrorMessage } from '../../inventory/shared/inventoryApi';
import { useT, type TextKey } from '../../../i18n';

/**
 * Administrar → Liberar espacio. Clientes sin movimiento (sin deuda, sin préstamos ni abonos en los últimos
 * meses, sin alertas) que tienen fotos o documentos: elegirlos los lleva a una papelera de 30 días que se
 * puede recuperar. Los datos del cliente, sus préstamos y sus abonos no se tocan.
 */
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

export const STORAGE_MENU = '/admin/storage';

interface Suggestion {
  id: number;
  name: string;
  document: string;
  route: { id: number; name: string } | null;
  lastMovement: string | null;
  files: number;
  bytes: number;
}

interface Lot {
  id: number;
  by: string | null;
  createdAt: string;
  clients: number;
  files: number;
  bytes: number;
  restoreUntil: string;
  status: 'trash' | 'restored' | 'expired' | 'purged';
}

/** Tamaño legible en el idioma de la empresa: «840 MB», «1,2 GB». */
export const formatSize = (bytes: number) => {
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${formatNumber(Math.max(0, mb), mb < 10 ? 1 : 0)} MB`;
  return `${formatNumber(mb / 1024, 1)} GB`;
};

export const formatDay = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(companyLocale(), { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${iso.slice(0, 10)}T12:00:00Z`));
  } catch {
    return iso.slice(0, 10);
  }
};

const MONTHS = [6, 12, 24];

/** Los textos con cantidad tienen versión «_one» y «_other». */
export const plural = (base: string, count: number) => `${base}_${count === 1 ? 'one' : 'other'}` as TextKey;

const StorageCleanup: React.FC<ShowAlerts> = () => {
  const t = useT();
  const showAlert = useAlert();
  const [loading, setLoading] = useState(true);
  setPageTitle('menu.admin.storage');
  const { canUpdate } = useResourcePermissions(STORAGE_MENU);
  const [months, setMonths] = useState(6);
  const [clients, setClients] = useState<Suggestion[]>([]);
  const [lots, setLots] = useState<Lot[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([axios.get('storage/cleanup/suggestions', { params: { months } }), axios.get('storage/cleanup')]);
      setClients(s.data?.clients ?? []);
      setLots(h.data?.data ?? []);
      setSelected([]);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('common.loadError')), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months]);

  useEffect(() => {
    void load();
  }, [load]);

  const chosen = useMemo(() => clients.filter((c) => selected.includes(c.id)), [clients, selected]);
  const totalBytes = clients.reduce((n, c) => n + c.bytes, 0);
  const chosenBytes = chosen.reduce((n, c) => n + c.bytes, 0);
  const chosenFiles = chosen.reduce((n, c) => n + c.files, 0);

  const freeUp = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post('storage/cleanup', { clientIds: chosen.map((c) => c.id), months });
      setConfirm(false);
      showAlert(t('storageCleanup.done', { size: formatSize(data?.data?.bytes ?? 0), date: formatDay(data?.data?.restoreUntil ?? null) }), 'success');
      await load();
    } catch (error) {
      showAlert(apiErrorMessage(error, t('common.saveError')), 'error');
    } finally {
      setBusy(false);
    }
  };

  const restore = async (lot: Lot) => {
    setBusy(true);
    try {
      await axios.post(`storage/cleanup/${lot.id}/restore`);
      showAlert(t('storageCleanup.restored'), 'success');
      await load();
    } catch (error) {
      showAlert(apiErrorMessage(error, t('common.saveError')), 'error');
    } finally {
      setBusy(false);
    }
  };

  const allSelected = clients.length > 0 && clients.every((c) => selected.includes(c.id));
  const toggle = (id: number) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const lotStatus = (l: Lot) =>
    l.status === 'trash'
      ? t('storageCleanup.status.trash', { date: formatDay(String(l.restoreUntil)) })
      : t(`storageCleanup.status.${l.status}` as 'storageCleanup.status.restored');

  return (
    <AdminPage>
      <PageHeader title={t('menu.admin.storage')} description={t('storageCleanup.subtitle')} />
      <Notice severity="info" dismissible>
        {t('storageCleanup.intro')}
      </Notice>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: 3 }, display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) auto' }, alignItems: 'center' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="p">
            {t(plural('storageCleanup.summary', clients.length), { count: clients.length, size: formatSize(totalBytes) })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selected.length
              ? t(plural('storageCleanup.selected', selected.length), { count: selected.length, files: chosenFiles, size: formatSize(chosenBytes) })
              : t('storageCleanup.pick')}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ minWidth: 0 }}>
          <TextField
            id="storage-months"
            select
            size="small"
            label={t('storageCleanup.months')}
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            sx={{ minWidth: { sm: 220 } }}
            data-tour="storage-months"
          >
            {MONTHS.map((m) => (
              <MenuItem key={m} value={m}>
                {t(plural('storageCleanup.monthsOption', m), { count: m })}
              </MenuItem>
            ))}
          </TextField>
          {canUpdate && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<CleaningServicesOutlinedIcon />}
              disabled={!selected.length}
              onClick={() => setConfirm(true)}
              data-tour="storage-free"
            >
              {t('storageCleanup.free')}
            </Button>
          )}
        </Stack>
      </Paper>
      <ContentCard>
        <Box sx={{ minWidth: 0 }} data-tour="storage-table">
          <DataTable<Suggestion>
            rows={clients}
            loading={loading}
            getRowId={(c) => c.id}
            ariaLabel={t('menu.admin.storage')}
            searchable
            initialSort={{ columnId: 'size', direction: 'desc' }}
            columns={[
              {
                id: 'name',
                header: canUpdate ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={selected.length > 0 && !allSelected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setSelected(e.target.checked ? clients.map((c) => c.id) : [])}
                      inputProps={{ 'aria-label': t('storageCleanup.selectAll') }}
                      sx={{ p: 0.5, ml: -0.5 }}
                    />
                    {t('storageCleanup.client')}
                  </Box>
                ) : (
                  t('storageCleanup.client')
                ),
                csvHeader: t('storageCleanup.client'),
                value: (c) => c.name,
                render: (c) =>
                  canUpdate ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Checkbox
                        size="small"
                        checked={selected.includes(c.id)}
                        onChange={() => toggle(c.id)}
                        inputProps={{ 'aria-label': `${t('storageCleanup.client')} ${c.name}` }}
                        sx={{ p: 0.5, ml: -0.5 }}
                      />
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.name}
                      </Box>
                    </Box>
                  ) : (
                    c.name
                  ),
                sortable: true,
              },
              { id: 'document', header: t('storageCleanup.document'), value: (c) => c.document, hideBelow: 'sm' },
              { id: 'route', header: t('storageCleanup.route'), value: (c) => c.route?.name ?? '—', sortable: true, hideBelow: 'md' },
              { id: 'last', header: t('storageCleanup.lastMovement'), value: (c) => c.lastMovement ?? '', render: (c) => formatDay(c.lastMovement), sortable: true, hideBelow: 'md' },
              { id: 'files', header: t('storageCleanup.files'), value: (c) => c.files, align: 'right', sortable: true, hideBelow: 'sm' },
              { id: 'size', header: t('storageCleanup.size'), value: (c) => c.bytes, render: (c) => formatSize(c.bytes), align: 'right', sortable: true },
            ]}
            emptyState={<EmptyState icon={<CleaningServicesOutlinedIcon />} title={t(plural('storageCleanup.empty', months), { count: months })} dense />}
          />
        </Box>
      </ContentCard>
      {lots.length > 0 && (
        <ContentCard>
          <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
            {t('storageCleanup.history')}
          </Typography>
          <DataTable<Lot>
            rows={lots}
            getRowId={(l) => l.id}
            ariaLabel={t('storageCleanup.history')}
            maxHeight={360}
            initialSort={{ columnId: 'date', direction: 'desc' }}
            columns={[
              { id: 'date', header: t('storageCleanup.lotDate'), value: (l) => String(l.createdAt), render: (l) => formatDay(String(l.createdAt)), sortable: true },
              { id: 'by', header: t('storageCleanup.lotBy'), value: (l) => l.by ?? '—', hideBelow: 'sm' },
              { id: 'clients', header: t('storageCleanup.lotClients'), value: (l) => l.clients, align: 'right' },
              { id: 'size', header: t('storageCleanup.size'), value: (l) => l.bytes, render: (l) => formatSize(l.bytes), align: 'right', hideBelow: 'sm' },
              {
                id: 'status',
                header: t('storageCleanup.lotStatus'),
                value: (l) => lotStatus(l),
                render: (l) => <StatusChip label={lotStatus(l)} tone={l.status === 'trash' ? 'warning' : l.status === 'restored' ? 'success' : 'neutral'} />,
              },
            ]}
            rowActions={(l) =>
              l.status === 'trash' && canUpdate ? (
                <Button size="small" startIcon={<RestoreFromTrashOutlinedIcon />} disabled={busy} onClick={() => restore(l)}>
                  {t('storageCleanup.restore')}
                </Button>
              ) : null
            }
          />
        </ContentCard>
      )}

      <StandardModal
        open={confirm}
        onClose={() => !busy && setConfirm(false)}
        title={t('storageCleanup.confirmTitle')}
        subtitle={t(plural('storageCleanup.selected', chosen.length), { count: chosen.length, files: chosenFiles, size: formatSize(chosenBytes) })}
        maxWidth={560}
        disableClose={busy}
        actions={
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 1 }}>
            <Button variant="outlined" onClick={() => setConfirm(false)} disabled={busy} color="inherit">
              {t('common.cancel')}
            </Button>
            <LoadingButton variant="contained" color="warning" onClick={freeUp} loading={busy}>
              {t('storageCleanup.confirm')}
            </LoadingButton>
          </Stack>
        }
      >
        <Stack spacing={1.5}>
          <Typography>{t('storageCleanup.confirmBody1')}</Typography>
          <Typography>{t('storageCleanup.confirmBody2')}</Typography>
          <Typography color="text.secondary" variant="body2">
            {t('storageCleanup.confirmBody3')}
          </Typography>
        </Stack>
      </StandardModal>
    </AdminPage>
  );
};

export default StorageCleanup;
